import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Loader2 } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token");
  const inviteEmail = searchParams.get("email");

  const [name, setName] = useState("");
  const [email, setEmail] = useState(inviteEmail || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (inviteEmail) setEmail(inviteEmail);
  }, [inviteEmail]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password, inviteToken);
      toast.success(inviteToken ? "Konto erstellt und Einladung angenommen" : "Konto erstellt. Willkommen!");
      navigate("/app");
    } catch (err) {
      const msg = formatDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F9F9F8]">
      <div className="hidden lg:block relative overflow-hidden bg-[#18322B]">
        <img alt="" src="https://images.pexels.com/photos/7794004/pexels-photo-7794004.jpeg" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#18322B]/85 via-[#18322B]/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center">
            <img
              src="/vorbuch-logo.png"
              alt="vorbuch.de"
              className="h-[7.5rem] w-auto"
              data-testid="register-logo"
            />
          </div>
          <div className="max-w-md">
            <h1 className="font-display font-extrabold text-4xl leading-tight tracking-tight">
              {inviteToken ? "Sie wurden eingeladen." : "Starten Sie in 30 Sekunden."}
            </h1>
            <p className="mt-4 text-sm opacity-80 leading-relaxed">
              {inviteToken
                ? "Erstellen Sie Ihr Konto und erhalten Sie sofort Zugriff auf den geteilten Arbeitsbereich."
                : "Ein Konto, mehrere Firmen. Kein Setup, kein Papierkram – nur hochladen und vorbuch.de erledigt den Rest."}
            </p>
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] opacity-60">© vorbuch.de · Made for Germany</div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 sm:px-12 py-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5E60] mb-2">
              {inviteToken ? "Einladung annehmen" : "Registrieren"}
            </div>
            <h2 className="font-display font-extrabold text-3xl tracking-tight text-[#1A1A1A]">Neues Konto anlegen</h2>
          </div>

          {inviteToken && (
            <div className="mb-4 rounded-md bg-[#E4F0E8] text-[#1E4620] text-xs px-3 py-2" data-testid="invite-banner">
              Sie wurden mit dieser E-Mail eingeladen. Nach der Registrierung erhalten Sie direkt Zugriff.
            </div>
          )}

          {error && <div className="mb-4 rounded-md bg-[#FCE8E8] text-[#B31D1D] text-xs px-3 py-2" data-testid="register-error">{error}</div>}

          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Name</label>
          <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2.5 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" placeholder="Max Mustermann" />

          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5 mt-4">E-Mail</label>
          <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            readOnly={!!inviteEmail}
            className="w-full px-3 py-2.5 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B] read-only:bg-[#F3F3F1]" placeholder="max@firma.de" />

          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5 mt-4">Passwort (min. 6 Zeichen)</label>
          <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]" placeholder="••••••••" />

          <button data-testid="register-submit" type="submit" disabled={loading}
            className="mt-6 w-full bg-[#18322B] hover:bg-[#21453B] text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-colors duration-150 disabled:opacity-50 flex items-center justify-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {inviteToken ? "Einladung annehmen" : "Konto erstellen"}
          </button>

          <div className="mt-6 text-center text-xs text-[#5C5E60]">
            Bereits registriert?{" "}
            <Link to="/login" className="font-semibold text-[#18322B] hover:underline" data-testid="link-login">Anmelden</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
